import { useCallback, useState } from "react";
import { toast } from "react-toastify";
import { validateFile } from "./constants";
import {
  downloadTemplate,
  downloadValidationResult,
  errorMessageFrom,
  uploadInventorFile,
} from "./inventorImportApi";

/**
 * Whether the caller's view needs reloading after an upload. Only a response that
 * explicitly reports nothing landed skips it: a body we cannot read is not evidence that
 * the rows were not created, and a refresh nobody needed is cheaper than inventors that
 * stay invisible until the page is reloaded.
 */
const anythingImported = (result) => {
  if (!result) return true;
  return (result.successCount || 0) + (result.partialCount || 0) > 0;
};

/**
 * The upload interaction itself — choosing a file, importing it, and fetching the
 * template or a validation result.
 *
 * `clientProfileId` is the client the upload runs against: every imported inventor is
 * attached to it (KOT-106 AC5).
 */
export const useInventorImport = ({ clientProfileId, onImported } = {}) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [summary, setSummary] = useState(null);

  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [downloadingBatchId, setDownloadingBatchId] = useState(null);
  const [downloadError, setDownloadError] = useState(null);

  const reset = useCallback(() => {
    setFile(null);
    setUploadError(null);
    setSummary(null);
    setDownloadError(null);
  }, []);

  /**
   * Selects a file and immediately starts importing it — used when a file is chosen or
   * dropped so the user doesn't have to click a separate Import button.
   */
  const selectAndUpload = useCallback(
    async (selected) => {
      // Clearing the previous summary keeps the counts on screen from ever describing a
      // different file than the one now selected.
      setSummary(null);
      setDownloadError(null);

      const problem = validateFile(selected);
      setUploadError(problem);
      if (problem) {
        setFile(null);
        return;
      }

      setFile(selected);
      setUploading(true);

      try {
        const result = await uploadInventorFile(selected, clientProfileId);
        // Falls back to an empty summary rather than letting a missing body throw: the
        // rows were imported, so reporting a failure would send the user to upload again.
        setSummary(result || {});
        setFile(null);
        if (onImported && anythingImported(result)) onImported(result);
      } catch (error) {
        // A rejected file (wrong template, unreadable, too many rows) comes back as a 400
        // with the reason. Show it next to the dropzone rather than as a toast the user
        // has to re-read to know what to fix.
        setUploadError(await errorMessageFrom(error, "Failed to import inventors."));
      } finally {
        setUploading(false);
      }
    },
    [clientProfileId, onImported],
  );

  /** A file the dropzone turned away, so it never reached `validateFile`. */
  const rejectFile = useCallback((message) => {
    setFile(null);
    setSummary(null);
    setUploadError(message);
  }, []);

  const clearFile = useCallback(() => {
    setFile(null);
    setUploadError(null);
  }, []);

  const fetchTemplate = useCallback(async () => {
    setDownloadingTemplate(true);
    try {
      await downloadTemplate();
    } catch (error) {
      toast.error(await errorMessageFrom(error, "Failed to download the template."));
    } finally {
      setDownloadingTemplate(false);
    }
  }, []);

  const fetchResult = useCallback(async (batchId) => {
    setDownloadingBatchId(batchId);
    setDownloadError(null);
    try {
      await downloadValidationResult(batchId);
    } catch (error) {
      setDownloadError(await errorMessageFrom(error, "Failed to download the validation result."));
    } finally {
      setDownloadingBatchId(null);
    }
  }, []);

  return {
    file,
    uploading,
    uploadError,
    summary,
    downloadingTemplate,
    downloadingBatchId,
    downloadError,
    reset,
    selectAndUpload,
    rejectFile,
    clearFile,
    fetchTemplate,
    fetchResult,
  };
};
